/**
 * Mock metrics data for StarNexus platform
 * @module data/starnexus/mockMetrics
 */

import type {
  SystemMetrics,
  SystemHealth,
  DashboardMetrics,
  ProcessingTrend
} from '../../types/starnexus';

// Helper to create dates relative to now
const daysAgo = (days: number): Date => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date;
};

// System health status
export const mockSystemHealth: SystemHealth = {
  status: 'healthy',
  message: 'All systems operational',
  lastChecked: new Date(),
  components: [
    { name: 'fileIngestion', status: 'healthy', latency: 45 },
    { name: 'dataProcessing', status: 'healthy', latency: 120 },
    { name: 'andesIntegration', status: 'healthy', latency: 200 },
    { name: 'storage', status: 'healthy', latency: 50 }
  ]
};

// System metrics
export const mockSystemMetrics: SystemMetrics = {
  totalDatasets: 12,
  datasetGrowth: 2,
  filesProcessed24h: 35890,
  filesProcessedToday: 1250,
  avgFilesPerHour: 52,
  globalSuccessRate: 97.8,
  activeUsers: 15,
  teamsActive: 4,
  queuedFiles: 15,
  processingFiles: 8,
  errorRate: 2.2,
  activeDatasets: 12
};

// Dashboard metrics with trends
export const mockDashboardMetrics: DashboardMetrics = {
  activeDatasets: {
    value: 12,
    change: 2,
    changeType: 'positive',
    label: 'Active Datasets'
  },
  filesProcessedToday: {
    value: 1250,
    change: 15,
    changeType: 'positive',
    label: 'Files Processed Today'
  },
  successRate: {
    value: 97.8,
    change: 0.5,
    changeType: 'positive',
    label: 'Success Rate',
    unit: '%'
  },
  avgProcessingTime: {
    value: 42,
    change: -3,
    changeType: 'positive', // Lower is better
    label: 'Avg Processing Time',
    unit: 's'
  }
};

// Processing trends for charts (last 7 days)
export const mockProcessingTrends: ProcessingTrend[] = [
  {
    date: daysAgo(6),
    filesProcessed: 980,
    successCount: 955,
    failureCount: 25,
    avgDuration: 45
  },
  {
    date: daysAgo(5),
    filesProcessed: 1120,
    successCount: 1095,
    failureCount: 25,
    avgDuration: 43
  },
  {
    date: daysAgo(4),
    filesProcessed: 1050,
    successCount: 1020,
    failureCount: 30,
    avgDuration: 48
  },
  {
    date: daysAgo(3),
    filesProcessed: 1180,
    successCount: 1155,
    failureCount: 25,
    avgDuration: 41
  },
  {
    date: daysAgo(2),
    filesProcessed: 1220,
    successCount: 1195,
    failureCount: 25,
    avgDuration: 40
  },
  {
    date: daysAgo(1),
    filesProcessed: 1150,
    successCount: 1125,
    failureCount: 25,
    avgDuration: 44
  },
  {
    date: new Date(),
    filesProcessed: 1250,
    successCount: 1222,
    failureCount: 28,
    avgDuration: 42
  }
];

// Processing volume by dataset (for monitoring dashboard)
export const mockDatasetVolumes = [
  {
    datasetId: 'ds-008',
    datasetName: 'website-analytics',
    volume: 12500,
    percentage: 34.8
  },
  {
    datasetId: 'ds-002',
    datasetName: 'inventory-updates',
    volume: 8500,
    percentage: 23.7
  },
  {
    datasetId: 'ds-004',
    datasetName: 'order-fulfillment',
    volume: 4200,
    percentage: 11.7
  },
  {
    datasetId: 'ds-011',
    datasetName: 'warehouse-shipments',
    volume: 3200,
    percentage: 8.9
  },
  {
    datasetId: 'ds-010',
    datasetName: 'support-tickets',
    volume: 1800,
    percentage: 5.0
  }
];

// Error rate trends (last 30 days for monitoring)
export const mockErrorRateTrends = Array.from({ length: 30 }, (_, i) => ({
  date: daysAgo(29 - i),
  errorRate: 1.5 + Math.random() * 2, // Random between 1.5% and 3.5%
  threshold: 5.0
}));

// Recent errors for monitoring dashboard
export const mockRecentErrors = [
  {
    errorId: 'err-001',
    datasetId: 'ds-006',
    datasetName: 'product-catalog',
    errorType: 'connection_timeout',
    message: 'Connection timeout while writing to Andes',
    timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
    count: 3
  },
  {
    errorId: 'err-002',
    datasetId: 'ds-003',
    datasetName: 'customer-feedback',
    errorType: 'schema_validation',
    message: 'Schema validation failed: missing required column',
    timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
    count: 1
  },
  {
    errorId: 'err-003',
    datasetId: 'ds-015',
    datasetName: 'returns-processing',
    errorType: 'data_format',
    message: 'Invalid date format in column "processed_at"',
    timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
    count: 2
  }
];

// Export helper functions
export const getMetricsByDateRange = (
  startDate: Date,
  endDate: Date
): ProcessingTrend[] => {
  return mockProcessingTrends.filter(
    (t) => t.date && t.date >= startDate && t.date <= endDate
  );
};

export const getTopDatasetsByVolume = (limit: number = 5) => {
  return mockDatasetVolumes.slice(0, limit);
};

export const getRecentErrors = (limit: number = 10) => {
  return mockRecentErrors.slice(0, limit);
};
